import { PasswordConfig, PasswordStrength, GeneratedPassword } from '../types/password.types';

export class PasswordService {
  private static readonly UPPERCASE = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  private static readonly LOWERCASE = 'abcdefghijklmnopqrstuvwxyz';
  private static readonly NUMBERS = '0123456789';
  private static readonly SYMBOLS = '!@#$%^&*()_+-=[]{}|;:,.<>?';
  private static readonly AMBIGUOUS_CHARS = '0Oo1Il|`';

  static generatePassword(config: PasswordConfig): string {
    let characterSet = '';
    let requiredChars = '';

    // Build character set based on config
    if (config.includeUppercase) {
      const chars = config.excludeAmbiguous 
        ? this.removeAmbiguousChars(this.UPPERCASE)
        : this.UPPERCASE;
      characterSet += chars;
      if (config.minNumbers || config.minSymbols) {
        requiredChars += this.getRandomChar(chars);
      }
    }

    if (config.includeLowercase) {
      const chars = config.excludeAmbiguous 
        ? this.removeAmbiguousChars(this.LOWERCASE)
        : this.LOWERCASE;
      characterSet += chars;
      if (config.minNumbers || config.minSymbols) {
        requiredChars += this.getRandomChar(chars);
      }
    }

    if (config.includeNumbers) {
      const chars = config.excludeAmbiguous 
        ? this.removeAmbiguousChars(this.NUMBERS)
        : this.NUMBERS;
      characterSet += chars;
      
      // Add minimum required numbers
      const minNums = config.minNumbers || 0;
      for (let i = 0; i < minNums; i++) {
        requiredChars += this.getRandomChar(chars);
      }
    }

    if (config.includeSymbols) {
      const symbols = config.customSymbols || this.SYMBOLS;
      const chars = config.excludeAmbiguous 
        ? this.removeAmbiguousChars(symbols)
        : symbols;
      characterSet += chars;
      
      // Add minimum required symbols
      const minSyms = config.minSymbols || 0;
      for (let i = 0; i < minSyms; i++) {
        requiredChars += this.getRandomChar(chars);
      }
    }

    if (!characterSet) {
      throw new Error('No character set selected');
    }

    // Generate password
    let password = requiredChars;
    const remainingLength = config.length - requiredChars.length;

    for (let i = 0; i < remainingLength; i++) {
      password += this.getRandomChar(characterSet);
    }

    // Shuffle the password to avoid predictable patterns
    return this.shuffleString(password);
  }

  static calculateStrength(password: string): PasswordStrength {
    let score = 0;
    
    // Length scoring
    if (password.length >= 8) score += 1;
    if (password.length >= 12) score += 1;
    if (password.length >= 16) score += 1;
    
    // Character variety scoring
    if (/[a-z]/.test(password)) score += 1;
    if (/[A-Z]/.test(password)) score += 1;
    if (/[0-9]/.test(password)) score += 1;
    if (/[^a-zA-Z0-9]/.test(password)) score += 1;
    
    // Complexity scoring
    if (password.length >= 10 && /[a-z]/.test(password) && /[A-Z]/.test(password) && /[0-9]/.test(password) && /[^a-zA-Z0-9]/.test(password)) {
      score += 1;
    }

    if (score <= 2) return PasswordStrength.VERY_WEAK;
    if (score <= 4) return PasswordStrength.WEAK;
    if (score <= 6) return PasswordStrength.FAIR;
    if (score <= 7) return PasswordStrength.GOOD;
    return PasswordStrength.STRONG;
  }

  static createGeneratedPassword(config: PasswordConfig): GeneratedPassword {
    const password = this.generatePassword(config);
    return {
      id: this.generateId(),
      value: password,
      strength: this.calculateStrength(password),
      config: { ...config },
      generatedAt: new Date(),
    };
  }

  static generateMultiplePasswords(config: PasswordConfig, count: number): GeneratedPassword[] {
    return Array.from({ length: count }, () => this.createGeneratedPassword(config));
  }

  private static removeAmbiguousChars(str: string): string {
    return str.split('').filter(char => !this.AMBIGUOUS_CHARS.includes(char)).join('');
  }

  private static getRandomChar(str: string): string {
    return str.charAt(Math.floor(Math.random() * str.length));
  }

  private static shuffleString(str: string): string {
    return str.split('').sort(() => Math.random() - 0.5).join('');
  }

  private static generateId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }
}
